import {
  Box,
  Button,
  ButtonDropdown,
  Container,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Select,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import UsersTable from './UsersTable/UsersTable';
import { useApi } from '@/src/store/api.context';
import { ConfirmModal } from '@/src/components/common/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { CognitoUser } from '@/src/types/User';
import { AuthRole } from '@/src/constants/shared';
import { preProdLogger } from '@/src/utils/log.utils';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { getGroupTitle } from '@/src/components/Settings/Sections/UsersTable/users-config';
import { CopyToClipboard } from '@/src/components/common/CopyToClipboard';

const ActionIDs = {
  ADD_USER_TO_AUTH_GROUP: 'add-user-to-auth-group',
  ADD_USER_TO_CUSTOM_AUTH_GROUP: 'add-user-to-custom-auth-group',
};

const generateAuthGroupOptions = () =>
  Object.values(AuthRole)
    .filter((group) => group !== AuthRole.JAM_ADMIN)
    .map((group) => ({ label: getGroupTitle(group), value: group }));

const authGroupOptions = generateAuthGroupOptions();

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CognitoUser[] | null>(null);
  const [lastSearch, setLastSearch] = useState<{ type: 'email' | 'vibeId'; term: string } | null>(null);
  const [showTableConfirmModal, setShowTableConfirmModal] = useState(false);
  const [confirmAddToAuthGroupUser, setConfirmAddToAuthGroupUser] = useState<CognitoUser | null>(null);
  const [confirmAddToCustomAuthGroupUser, setConfirmAddToCustomAuthGroupUser] = useState<CognitoUser | null>(null);
  const [customAuthGroup, setCustomAuthGroup] = useState('');
  const [selectedAuthGroupOption, setSelectedAuthGroupOption] = useState<OptionDefinition>({ value: '' });
  const [confirmRemoveGroupUser, setConfirmRemoveGroupUser] = useState<CognitoUser | null>(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const { accountApi } = useApi();

  const refreshResults = async () => {
    if (lastSearch) {
      const res = await accountApi.listUsers({ [lastSearch.type]: lastSearch.term });
      setResults(res.userResponses ?? null);
    }
  };

  const removeUserFromGroup = async (group: string) => {
    setLoading(true);

    try {
      if (group && confirmRemoveGroupUser?.vibeId) {
        await accountApi.removeUserFromGroup(confirmRemoveGroupUser?.vibeId, group);
        await refreshResults();
      }
    } catch (err: any) {
      preProdLogger('Error removing user from auth group', err.message);
    }

    setLoading(false);
    cleanupModals();
  };

  const addUserToGroup = async (user: CognitoUser | null, authGroup: string) => {
    if (authGroup) {
      setLoading(true);

      try {
        if (user?.vibeId && authGroup) {
          await accountApi.addUserToGroup(user?.vibeId, authGroup);
          await refreshResults();
        }
      } catch (err: any) {
        preProdLogger('Erroring adding user to auth group', err.message);
      }

      setLoading(false);
      cleanupModals();
    }
  };

  const handleRemoveGroupClick = (group: string, user: CognitoUser) => {
    setSelectedGroup(group);
    setConfirmRemoveGroupUser(user);
  };

  const cleanupModals = () => {
    setSelectedGroup('');
    setSelectedAuthGroupOption({ value: '' });
    setCustomAuthGroup('');
    setConfirmRemoveGroupUser(null);
    setConfirmAddToAuthGroupUser(null);
    setConfirmAddToCustomAuthGroupUser(null);
  };

  const handleActions = (id: string, user: CognitoUser) => {
    switch (id) {
      case ActionIDs.ADD_USER_TO_AUTH_GROUP:
        setConfirmAddToAuthGroupUser(user);
        break;
      case ActionIDs.ADD_USER_TO_CUSTOM_AUTH_GROUP:
        setConfirmAddToCustomAuthGroupUser(user);
        break;
      default:
        return;
    }
  };
  return (
    <SpaceBetween direction="vertical" size="m">
      <FormField
        secondaryControl={
          <ButtonDropdown
            items={[
              {
                text: t(i18nKeys.settings.userManagement.buttons.searchByVibeId) ?? '',
                id: 'search-by-id',
              },
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'search-by-id' && !!searchTerm) {
                setLoading(true);
                setLastSearch({ type: 'vibeId', term: searchTerm });
                accountApi
                  .listUsers({ vibeId: searchTerm })
                  .then((res) => setResults(res.userResponses ?? null))
                  .finally(() => setLoading(false));
              }
            }}
            disabled={!searchTerm}
            mainAction={{
              text: t(i18nKeys.settings.userManagement.buttons.searchByEmail),
              onClick: () => {
                if (!!searchTerm) {
                  setLoading(true);
                  setLastSearch({ type: 'email', term: searchTerm });
                  accountApi
                    .listUsers({ email: searchTerm })
                    .then((res) => setResults(res.userResponses ?? null))
                    .finally(() => setLoading(false));
                }
              },
              disabled: !searchTerm,
            }}
          />
        }
      >
        <Input value={searchTerm} onChange={({ detail }) => setSearchTerm(detail.value)} />
      </FormField>
      {!!results &&
        !loading &&
        results.map((userItem) => (
          <Container
            key={`result-${userItem?.login || userItem?.email}`}
            variant="stacked"
            header={
              <Header
                actions={
                  <ButtonDropdown
                    items={[
                      {
                        id: 'add-user-to-auth-group',
                        text: t(i18nKeys.settings.userManagement.buttons.addUserToAuthGroup) ?? '',
                      },
                      {
                        id: 'add-user-to-custom-auth-group',
                        text: t(i18nKeys.settings.userManagement.buttons.addUserToCustomAuthGroup) ?? '',
                      },
                    ]}
                    onItemClick={(ele) => handleActions(ele.detail.id, userItem)}
                    variant="icon"
                  />
                }
              >
                {userItem?.email ?? t(i18nKeys.settings.userManagement.labels.noEmail)}
              </Header>
            }
          >
            <KeyValuePairs
              columns={3}
              items={[
                {
                  label: t(i18nKeys.settings.userManagement.labels.name),
                  value: `${userItem.firstName ?? userItem.givenName ?? ''} ${
                    userItem?.lastName ?? userItem?.familyName ?? ''
                  }`,
                },
                {
                  label: t(i18nKeys.settings.userManagement.labels.roles),
                  value: (
                    <div>
                      {(userItem?.roles || []).map((role: string) => (
                        <div key={role}>
                          {role}{' '}
                          <Button
                            iconName="remove"
                            variant="icon"
                            onClick={() => handleRemoveGroupClick(role, userItem)}
                          />
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  label: t(i18nKeys.settings.userManagement.labels.vibeId),
                  value: (
                    <div>
                      {userItem.vibeId}
                      <CopyToClipboard value={`${userItem.vibeId}`} icon />
                    </div>
                  ),
                },
                {
                  label: t(i18nKeys.settings.userManagement.labels.provider),
                  value: userItem?.publicProviderName ?? userItem?.providerName ?? '',
                },
                {
                  label: t(i18nKeys.settings.userManagement.labels.legacyLogin),
                  value: (
                    <div>
                      {userItem?.legacyLogin ?? userItem?.login ?? ''}
                      <CopyToClipboard value={`${userItem?.legacyLogin ?? userItem?.login ?? ''}`} icon />
                    </div>
                  ),
                },
                {
                  label: t(i18nKeys.settings.userManagement.table.headers.emailVerified),
                  value: JSON.stringify(!!userItem?.emailVerified),
                },
                {
                  label: t(i18nKeys.settings.userManagement.table.headers.status),
                  value: userItem?.status,
                },
              ]}
            />
          </Container>
        ))}
      {loading && <Spinner />}
      {!showTable && (
        <Box>
          <Button onClick={() => setShowTableConfirmModal(true)}>
            {t(i18nKeys.settings.userManagement.buttons.loadAllUsersTable)}
          </Button>
        </Box>
      )}
      {!!showTable && (
        <UsersTable
          onUsersSelect={(users?: CognitoUser[] | null) => {
            if (!!users) {
              setResults(users ?? null);
            }
          }}
        />
      )}
      <ConfirmModal
        visible={showTableConfirmModal}
        onConfirm={() => {
          setShowTable(true);
          setShowTableConfirmModal(false);
        }}
        onCancel={() => setShowTableConfirmModal(false)}
        onDismiss={() => setShowTableConfirmModal(false)}
        message={t(i18nKeys.settings.userManagement.modals.loadAllUsers.message)}
        title={t(i18nKeys.settings.userManagement.modals.loadAllUsers.title)}
      />
      <ConfirmModal
        title={t(i18nKeys.settings.userManagement.modals.removeUserFromAuthGroup.title)}
        message={
          !!confirmRemoveGroupUser?.cognitoUsername
            ? t(i18nKeys.settings.userManagement.modals.removeUserFromAuthGroup.message, {
                username: confirmRemoveGroupUser?.cognitoUsername,
                authGroup: getGroupTitle(selectedGroup),
              })
            : ''
        }
        confirmBtnLabel={t(i18nKeys.settings.userManagement.buttons.yesContinue)}
        onConfirm={() => removeUserFromGroup(selectedGroup)}
        visible={confirmRemoveGroupUser !== null}
        onCancel={() => cleanupModals()}
      />
      <ConfirmModal
        title={t(i18nKeys.settings.userManagement.modals.addUserToAuthGroup.title)}
        message={
          <>
            {!!confirmAddToAuthGroupUser?.cognitoUsername && (
              <div>
                {t(i18nKeys.settings.userManagement.modals.addUserToAuthGroup.message, {
                  username: confirmAddToAuthGroupUser?.cognitoUsername,
                })}
              </div>
            )}
            <Select
              options={authGroupOptions}
              selectedOption={selectedAuthGroupOption}
              onChange={({ detail }) => setSelectedAuthGroupOption(detail.selectedOption)}
            />
          </>
        }
        disabled={!selectedAuthGroupOption?.value}
        onCancel={() => cleanupModals()}
        onConfirm={() => addUserToGroup(confirmAddToAuthGroupUser, selectedAuthGroupOption?.value || '')}
        visible={confirmAddToAuthGroupUser != null}
        data-testid="add-auth-group-modal"
      />
      <ConfirmModal
        title={t(i18nKeys.settings.userManagement.modals.addUserToCustomAuthGroup.title)}
        visible={confirmAddToCustomAuthGroupUser !== null}
        onCancel={() => cleanupModals()}
        onConfirm={() => addUserToGroup(confirmAddToCustomAuthGroupUser, customAuthGroup)}
        disabled={!customAuthGroup}
        message={
          <>
            {!!confirmAddToCustomAuthGroupUser?.cognitoUsername && (
              <div>
                {t(i18nKeys.settings.userManagement.modals.addUserToCustomAuthGroup.message, {
                  username: confirmAddToCustomAuthGroupUser?.cognitoUsername,
                })}
              </div>
            )}
            <Input type="text" value={customAuthGroup} onChange={({ detail }) => setCustomAuthGroup(detail.value)} />
          </>
        }
        data-testid="add-custom-auth-group-modal"
      />
    </SpaceBetween>
  );
};

export default UserManagement;
